import { ClaimsSchema, ClaimsSchemaFieldsItem, CredentialClaimValue, CredentialClaimValues } from '../../api';
import * as serialization from '../../serialization';
import {
    ClaimDescriptor,
    getRegisteredClaimsList,
    getTsDataTypeMetadata,
    getVcSchemaMetadataOrThrow,
} from '../decorators';
import { UdtError, UdtErrorCode } from '../error';
import { LinkedCredential, LinkedFile, Rfc3339Date, Rfc3339DateTime } from '../models';
import {
    ApiClient,
    Claims,
    Ctor,
    isLinkedEntityDescriptor,
    isValueWithDataId,
    isValueWithId,
    ReadClaims,
    ReadDraftClaims,
    UserUpdateClaims,
} from '../types';
import { StrictPartial, strictlyConvertToApiType } from '../utils/converters';
import { Credential } from './credential';
import { DraftCredential } from './draft/draft';
import { ClaimsSchemaFieldValue, Json } from './types';
import { VerifiableCredential } from './verifiable/verifiable';

export class UdtClaims<T extends Claims> {
    constructor(
        protected client: ApiClient,
        protected claimsModel: Ctor<T>,
    ) {}

    private processValues<K extends Claims>(
        model: Ctor<any>,
        input: Json,
        processValue: (descriptor: ClaimDescriptor, value: unknown) => unknown,
        initialValue: K,
    ): K {
        return getRegisteredClaimsList(model).reduce((result, descriptor) => {
            if (descriptor.name in input) {
                const value: unknown = input[descriptor.name];
                let processedValue: unknown;

                if (descriptor.isArray) {
                    if (value == null) {
                        processedValue = null;
                    } else if (Array.isArray(value)) {
                        processedValue = value.map((item) => {
                            return processValue(descriptor, item);
                        });
                    } else {
                        throw new UdtError(
                            UdtErrorCode.UnexpectedTypeInSerialization,
                            `Can't convert received value as it has an unexpected type. The value is defined in UDT as "Array".`,
                        );
                    }
                } else {
                    processedValue = processValue(descriptor, value);
                }
                Reflect.set(result, descriptor.name, processedValue);
            }

            return result;
        }, initialValue);
    }

    private processUdt<K extends Claims>(
        model: Ctor<any>,
        input: K,
        processValue: (descriptor: ClaimDescriptor, value: unknown) => CredentialClaimValue,
        initialValue: CredentialClaimValues,
    ): CredentialClaimValues {
        return getRegisteredClaimsList(model).reduce((result, descriptor) => {
            if (descriptor.name in input) {
                const value: unknown = input[descriptor.name];
                let processedValue: CredentialClaimValue | null;

                if (value == null) {
                    return result;
                }
                if (descriptor.isArray) {
                    if (Array.isArray(value)) {
                        processedValue = {
                            kind: 'ARRAY',
                            value: value.map((item) => {
                                return processValue(descriptor, item);
                            }),
                        };
                    } else {
                        throw new UdtError(
                            UdtErrorCode.UnexpectedTypeInSerialization,
                            `Can't convert received value as it has an unexpected type. The value is defined in UDT as "Array".`,
                        );
                    }
                } else {
                    processedValue = processValue(descriptor, value);
                }
                Reflect.set(result, descriptor.name, processedValue);
            }

            return result;
        }, initialValue);
    }

    private processCredentialClaimValues<K extends Claims>(
        model: Ctor<any>,
        input: CredentialClaimValues,
        processValue: (descriptor: ClaimDescriptor, value: CredentialClaimValue) => unknown,
        initialValue: K,
    ): K {
        return getRegisteredClaimsList(model).reduce((result, descriptor) => {
            if (descriptor.name in input) {
                const value = input[descriptor.name];
                let processedValue: unknown;

                if (descriptor.isArray) {
                    if (value == null) {
                        processedValue = null;
                    } else if (value.kind === 'ARRAY') {
                        processedValue = value.value.map((item) => {
                            return processValue(descriptor, item);
                        });
                    } else {
                        throw new UdtError(
                            UdtErrorCode.UnexpectedTypeInSerialization,
                            `Can't convert received value as it has an unexpected type. The value is defined in UDT as "Array".`,
                        );
                    }
                } else {
                    processedValue = processValue(descriptor, value);
                }
                Reflect.set(result, descriptor.name, processedValue);
            }

            return result;
        }, initialValue);
    }

    private getClaimTypeInfo(model: Ctor<any>, descriptor: ClaimDescriptor): Ctor<any> {
        const typeInfo = descriptor.nestedType ? descriptor.nestedType : getTsDataTypeMetadata(model, descriptor.name);

        if (typeInfo == null) {
            throw new Error(
                `Can't convert received value. Inconsistent data definition. Please reach out to support for assistance.`,
            );
        }

        return typeInfo;
    }

    /** convertCredentialClaimValuesToUdt maps and converts the API received claims values to the UDT claims model */
    protected convertCredentialClaimValuesToUdt<K extends Claims>(model: Ctor<any>, input: CredentialClaimValues): K {
        const processValue = (descriptor: ClaimDescriptor, value: CredentialClaimValue): unknown => {
            if (value == null) {
                return null;
            }

            const typeInfo = this.getClaimTypeInfo(model, descriptor);

            if (typeInfo === LinkedCredential) {
                if (value.kind !== 'LINK') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "LinkedCredential".`,
                    );
                }
                return new LinkedCredential(this.client, value.value.id, value.value.digestMultibase);
            }

            if (typeInfo === LinkedFile) {
                if (value.kind !== 'FILE') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "LinkedFile".`,
                    );
                }
                return new LinkedFile(this.client, value.value.id, value.value.digestMultibase);
            }

            if (typeInfo === Date) {
                if (value.kind !== 'DATETIME') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "Date".`,
                    );
                }
                return new Date(value.value);
            }

            if (typeInfo === Rfc3339DateTime) {
                if (value.kind !== 'DATETIME') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "Rfc3339DateTime".`,
                    );
                }
                return new Rfc3339DateTime(value.value);
            }

            if (typeInfo === Rfc3339Date) {
                if (value.kind !== 'DATE') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "Rfc3339Date".`,
                    );
                }
                return new Rfc3339Date(value.value);
            }

            if (typeInfo === String) {
                if (value.kind !== 'STRING') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "string".`,
                    );
                }

                return value.value;
            }

            if (typeInfo === Number) {
                if (value.kind !== 'NUMBER') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "number".`,
                    );
                }

                return value.value;
            }

            if (typeInfo === Boolean) {
                if (value.kind !== 'BOOLEAN') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "boolean".`,
                    );
                }

                return value.value;
            }

            if (value.kind !== 'STRUCT') {
                throw new UdtError(
                    UdtErrorCode.UnexpectedTypeInSerialization,
                    `Can't convert received value as it has an unexpected type. The value is defined in UDT as "${typeInfo.name}".`,
                );
            }

            return this.convertCredentialClaimValuesToUdt(typeInfo, value.value);
        };

        return this.processCredentialClaimValues<K>(model, input, processValue, new model());
    }

    /** convertApiValuesToUdt maps and converts the API received claims values to the UDT claims model */
    protected convertApiValuesToUdt<K extends Claims>(model: Ctor<any>, input: Json): K {
        const processValue = (descriptor: ClaimDescriptor, value: unknown): unknown => {
            if (value == null) {
                return null;
            }

            const typeInfo = this.getClaimTypeInfo(model, descriptor);

            if (isLinkedEntityDescriptor(value)) {
                if (typeInfo === LinkedCredential) {
                    return new LinkedCredential(this.client, value.id, value.digestMultibase);
                }

                if (typeInfo === LinkedFile) {
                    return new LinkedFile(this.client, value.id, value.digestMultibase);
                }
            }

            if (typeInfo === Date) {
                if (typeof value !== 'string') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "Date".`,
                    );
                }
                return new Date(value);
            }

            if (typeInfo === Rfc3339DateTime) {
                if (typeof value !== 'string') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "Rfc3339DateTime".`,
                    );
                }
                return new Rfc3339DateTime(value);
            }

            if (typeInfo === Rfc3339Date) {
                if (typeof value !== 'string') {
                    throw new UdtError(
                        UdtErrorCode.UnexpectedTypeInSerialization,
                        `Can't convert received value as it has an unexpected type. The value is defined in UDT as "Rfc3339Date".`,
                    );
                }
                return new Rfc3339Date(value);
            }

            // Primitive values should be returned as-is
            if (Array.of<Ctor<any>>(String, Number, Boolean, Object).includes(typeInfo)) {
                return value;
            }

            if (!(value instanceof Object)) {
                throw new UdtError(
                    UdtErrorCode.UnexpectedTypeInSerialization,
                    `Can't convert received value as it has an unexpected type. The value is defined in UDT as "${typeInfo.name}".`,
                );
            }
            return this.convertApiValuesToUdt(typeInfo, value);
        };

        return this.processValues<K>(model, input, processValue, new model());
    }

    /** convertUdtToApiValues maps and converts the user received claims values onto UDT claims model */
    protected convertUdtToCredentialClaimValues<K extends Claims>(model: Ctor<any>, input: K): CredentialClaimValues {
        const processValue = (descriptor: ClaimDescriptor, value: unknown): CredentialClaimValue => {
            const typeInfo = this.getClaimTypeInfo(model, descriptor);

            if (typeInfo === LinkedCredential) {
                if (value instanceof LinkedCredential) {
                    return value.toJSON();
                }

                if (
                    value instanceof VerifiableCredential ||
                    value instanceof DraftCredential ||
                    value instanceof Credential
                ) {
                    return value.toLinkedCredential().toJSON();
                }

                if (isValueWithId(value)) {
                    return LinkedCredential.createCredentialClaimValue(value.id, value.digestMultibase);
                }

                throw new UdtError(UdtErrorCode.UnexpectedTypeInSerialization, 'Invalid value for linked credential');
            }

            if (typeInfo === LinkedFile) {
                if (value instanceof LinkedFile) {
                    return value.toJSON();
                }

                if (isValueWithDataId(value)) {
                    return LinkedFile.createCredentialClaimValue(value.data.id, value.data.digestMultibase);
                }

                if (isValueWithId(value)) {
                    return LinkedFile.createCredentialClaimValue(value.id, value.digestMultibase);
                }

                throw new UdtError(UdtErrorCode.UnexpectedTypeInSerialization, 'Invalid value for linked file');
            }

            if (typeInfo === String && typeof value === 'string') {
                return {
                    kind: 'STRING',
                    value,
                };
            }

            if (typeInfo === Number && typeof value === 'number') {
                return {
                    kind: 'NUMBER',
                    value,
                };
            }

            if (typeInfo === Date && value instanceof Date) {
                return {
                    kind: 'DATETIME',
                    value,
                };
            }

            if (typeInfo === Rfc3339Date && value instanceof Rfc3339Date) {
                return {
                    kind: 'DATE',
                    value: value.toString(),
                };
            }

            if (typeInfo === Rfc3339DateTime && value instanceof Rfc3339DateTime) {
                return {
                    kind: 'DATETIME',
                    value: value.toDate(),
                };
            }

            if (typeInfo === Boolean && typeof value === 'boolean') {
                return {
                    kind: 'BOOLEAN',
                    value,
                };
            }

            if (!(value instanceof Object)) {
                throw new UdtError(
                    UdtErrorCode.UnexpectedTypeInSerialization,
                    `Can't convert received value as it has an unexpected type. The value is defined in UDT as "${typeInfo.name}".`,
                );
            }

            return {
                kind: 'STRUCT',
                value: this.convertUdtToCredentialClaimValues(typeInfo, value),
            };
        };
        return this.processUdt(model, input, processValue, {});
    }

    protected mapClaimDescriptorToClaimsSchemaFieldItem(
        claim: ClaimDescriptor,
        claimsModel: Ctor<any>,
    ): ClaimsSchemaFieldsItem {
        const draftBaseFieldValue: StrictPartial<ClaimsSchemaFieldValue> = {
            name: claim.name,
            title: claim.title,
            notEmpty: claim.notEmpty,
            isArray: claim.isArray || undefined,
        };

        if (
            claim.nestedType &&
            !Array.of<Ctor<any>>(
                String,
                Number,
                Date,
                Rfc3339DateTime,
                Rfc3339Date,
                Boolean,
                LinkedFile,
                LinkedCredential,
            ).includes(claim.nestedType)
        ) {
            const claimNestedType = claim.nestedType;
            const nestedVcSchemaMetadata = getVcSchemaMetadataOrThrow(claimNestedType);

            return strictlyConvertToApiType(serialization.ClaimsSchemaFieldsItem, {
                ...draftBaseFieldValue,
                kind: 'STRUCT',
                fields: getRegisteredClaimsList(claimNestedType).map((nestedClaim) => {
                    return this.mapClaimDescriptorToClaimsSchemaFieldItem(nestedClaim, claimNestedType);
                }),
                jsonLdIri: undefined,
                jsonLdBaseIri: nestedVcSchemaMetadata.jsonLdBaseIri,
                jsonLdTermName: nestedVcSchemaMetadata.jsonLdTermName,
                jsonLdRelativeIriDelimiter: undefined,
            });
        }

        const tsDataType = claim.nestedType || getTsDataTypeMetadata(claimsModel, claim.name);

        if (!tsDataType) {
            throw new Error(
                `The information about the VC claim data type was not found for the "${claim.name}" property. Ensure that "emitDecoratorMetadata" is enabled in your "tsconfig.json".`,
            );
        }

        switch (tsDataType) {
            case String:
                return strictlyConvertToApiType(serialization.ClaimsSchemaFieldsItem, {
                    ...draftBaseFieldValue,
                    kind: 'STRING',
                    enum: claim.enumValuesDescriptor,
                    jsonLdIri: claim.jsonLdIri,
                    jsonLdType: claim.jsonLdType,
                });
            case Number:
                return strictlyConvertToApiType(serialization.ClaimsSchemaFieldsItem, {
                    ...draftBaseFieldValue,
                    kind: 'NUMBER',
                    jsonLdIri: claim.jsonLdIri,
                    jsonLdType: claim.jsonLdType,
                });
            case Boolean:
                return strictlyConvertToApiType(serialization.ClaimsSchemaFieldsItem, {
                    ...draftBaseFieldValue,
                    kind: 'BOOLEAN',
                    jsonLdIri: claim.jsonLdIri,
                    jsonLdType: claim.jsonLdType,
                });
            case Rfc3339Date:
                return strictlyConvertToApiType(serialization.ClaimsSchemaFieldsItem, {
                    ...draftBaseFieldValue,
                    kind: 'DATE',
                    jsonLdIri: claim.jsonLdIri,
                    jsonLdType: claim.jsonLdType,
                });
            case Rfc3339DateTime:
            case Date:
                return strictlyConvertToApiType(serialization.ClaimsSchemaFieldsItem, {
                    ...draftBaseFieldValue,
                    kind: 'DATETIME',
                    jsonLdIri: claim.jsonLdIri,
                    jsonLdType: claim.jsonLdType,
                });
            case LinkedFile:
                return strictlyConvertToApiType(serialization.ClaimsSchemaFieldsItem, {
                    ...draftBaseFieldValue,
                    kind: 'FILE',
                });
            case LinkedCredential:
                return strictlyConvertToApiType(serialization.ClaimsSchemaFieldsItem, {
                    ...draftBaseFieldValue,
                    kind: 'LINK',
                    linkType: undefined,
                });
            default:
                throw new UdtError(
                    UdtErrorCode.UnsupportedTypeInUDT,
                    `Unsupported data type: ${tsDataType.name}. Please use one of the supported types.`,
                );
        }
    }

    /**
     * Marshalling is the process of transforming the internal representation of values to a data format suitable
     * for transmission.
     */
    marshalValues(input: UserUpdateClaims<T>): Claims {
        return this.convertUdtToCredentialClaimValues(this.claimsModel, input);
    }

    /**
     * Unmarshalling is the reverse process of marshalling. It involves converting data from a serialized format back
     * into an internal representation.
     */
    unmarshalValuesToDraft(input: CredentialClaimValues): ReadDraftClaims<T> {
        return this.convertCredentialClaimValuesToUdt(this.claimsModel, input);
    }

    unmarshalValuesToCredential(input: Json): ReadClaims<T> {
        return this.convertApiValuesToUdt(this.claimsModel, input);
    }

    getClaimsSchema(): ClaimsSchema {
        const vcSchemaMetadata = getVcSchemaMetadataOrThrow(this.claimsModel);

        return strictlyConvertToApiType(serialization.ClaimsSchema, {
            fields: getRegisteredClaimsList(this.claimsModel).map((claim) =>
                this.mapClaimDescriptorToClaimsSchemaFieldItem(claim, this.claimsModel),
            ),
            jsonLdBaseIri: vcSchemaMetadata.jsonLdBaseIri,
            jsonLdIri: undefined,
            jsonLdTermName: vcSchemaMetadata.jsonLdTermName,
            jsonLdRelativeIriDelimiter: undefined,
        });
    }
}
